import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';

const SHAPE_EMOJI = { bird: '🐦', horse: '🐎', boat: '⛵', city: '🏯' };

/**
 * 古诗拼图:诗句的每个字按预设坐标摆成鸟/马/船/城的松散轮廓,挖空的字需要
 * 从候选字里点选填入正确坑位。交互沿用项目里"先选中候选字、再点坑位放入;
 * 点已填坑位收回"的两次点击模式(和 SpatialPuzzle3D/CategorySortPuzzle 一致),
 * 不用拖拽。
 */
function PoemPuzzle({ puzzle, onComplete }) {
  const { shape, poem, layout, candidates } = puzzle.params;
  const [placements, setPlacements] = useState({}); // slotId -> candidateId
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // 按五言(或七言)切行;不整除时退回每行 5 字
  const perRow = layout.length % 5 === 0 ? 5 : layout.length % 7 === 0 ? 7 : 5;
  const rows = [];
  for (let i = 0; i < layout.length; i += perRow) rows.push(layout.slice(i, i + perRow));
  const blankEntries = layout.filter((entry) => entry.blank);
  const placedCandidateIds = new Set(Object.values(placements));
  const availableCandidates = candidates.filter((c) => !placedCandidateIds.has(c.id));
  const allFilled = blankEntries.every((entry) => placements[entry.slotId]);

  useEffect(() => {
    if (allFilled) {
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.4 } });
      const timer = setTimeout(() => onComplete(placements), 900);
      return () => clearTimeout(timer);
    }
  }, [allFilled, placements, onComplete]);

  const handleCandidateClick = (candidateId) => {
    setSelectedCandidateId((prev) => (prev === candidateId ? null : candidateId));
  };

  const handleSlotClick = (slotId) => {
    // 已填坑位再点一次 = 收回候选字(零挫败,可无限重试)
    if (placements[slotId]) {
      setPlacements((prev) => {
        const next = { ...prev };
        delete next[slotId];
        return next;
      });
      return;
    }
    if (selectedCandidateId) {
      setPlacements((prev) => ({ ...prev, [slotId]: selectedCandidateId }));
      setSelectedCandidateId(null);
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-6 rounded-2xl border-2 border-black bg-white p-4 shadow-brutal sm:p-8">
      <div className="text-center">
        <p className="text-lg font-bold text-gray-800">{puzzle.title}</p>
        <p className="text-sm text-gray-600">
          {poem.dynasty}·{poem.author}《{poem.title}》
        </p>
      </div>

      {/* 诗句按「每行 5/7 字」顺序排成整齐的格子(layout 数组本身就是阅读顺序),
          不再用 x/y 百分比摆轮廓——375px 下相邻字间距只有 25px,会互相压住。
          slotId 与判分用的 placements 不变。 */}
      <div
        className={`relative flex w-full max-w-sm flex-col gap-2 rounded-xl border-2 border-dashed
                    border-gray-300 bg-amber-50/40 p-3 ${allFilled ? 'animate-poem-gold-flash' : ''}`}
      >
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-8xl opacity-10">
          {SHAPE_EMOJI[shape] || '📜'}
        </span>
        {rows.map((row, r) => (
          <div key={r} className="relative flex justify-center gap-2">
            {row.map((entry, c) => {
              if (!entry.blank) {
                return (
                  <span
                    key={c}
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-white/80
                               font-serif text-2xl font-bold text-gray-800"
                  >
                    {entry.char}
                  </span>
                );
              }
              const filledCandidate = candidates.find((cd) => cd.id === placements[entry.slotId]);
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`slot-${entry.slotId}`}
                  onClick={() => handleSlotClick(entry.slotId)}
                  className={`flex h-11 w-11 items-center justify-center rounded-full border-2 font-serif
                              text-2xl font-bold transition
                              ${
                                filledCandidate
                                  ? 'border-black bg-qiji-gold text-gray-900 shadow-brutal-sm'
                                  : 'border-dashed border-gray-500 bg-white text-gray-400'
                              }`}
                >
                  {filledCandidate ? filledCandidate.char : '?'}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex w-full flex-wrap justify-center gap-3 pt-1">
        {availableCandidates.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            data-testid={`candidate-${candidate.id}`}
            onClick={() => handleCandidateClick(candidate.id)}
            className={`flex h-12 w-12 items-center justify-center rounded-lg border-2
                        border-black font-serif text-2xl font-bold shadow-brutal-sm transition
                        hover:-translate-y-0.5 active:translate-y-0 active:shadow-none
                        ${selectedCandidateId === candidate.id ? 'bg-qiji-mint' : 'bg-amber-50'}`}
          >
            {candidate.char}
          </button>
        ))}
      </div>

      <p className="text-center text-sm text-gray-600">
        先点一个候选字选中,再点图上带"?"的坑位放进去;放错了点一下坑位就能收回。
      </p>
    </div>
  );
}

export default PoemPuzzle;
